import { useRef, useState } from 'react';
import { useNotifications } from './useNotifications';

export interface ApiResult {
  data?: unknown;
  error?: string;
}

export function useApi() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult | null>(null);
  const { addNotification } = useNotifications();

  // Only the latest call may set state, so a slow earlier response (e.g. the previous app's) cannot overwrite it.
  const latestCall = useRef(0);

  /**
   * `silent` skips the success notification (e.g. for automatic loads); errors are always reported.
   * `keepResult` leaves the previous result on screen until the new one arrives (background refresh).
   */
  const execute = async (
    fn: () => Promise<unknown>,
    { silent = false, keepResult = false }: { silent?: boolean; keepResult?: boolean } = {}
  ) => {
    const call = ++latestCall.current;
    // A failed background refresh (keepResult) only notifies, so the table already on screen stays.
    const fail = (msg: string) => {
      if (!keepResult) setResult({ error: msg });
      addNotification('error', msg);
    };
    setLoading(true);
    if (!keepResult) setResult(null);
    try {
      const data = await fn();
      if (call !== latestCall.current) return;
      const d = data as Record<string, unknown>;
      if (d?.error) {
        const reason = d.reason as string | undefined;
        fail(reason ? `${d.error}: ${reason}` : JSON.stringify(data));
      } else {
        setResult({ data });
        if (!silent) addNotification('success', 'Operation completed successfully');
      }
    } catch (e) {
      if (call !== latestCall.current) return;
      fail((e as Error).message);
    } finally {
      if (call === latestCall.current) setLoading(false);
    }
  };

  return { loading, result, execute };
}
